import React from 'react';
import { Field, ErrorMessage } from 'formik';
import { Typography, Row, Col, Divider, Select } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { colors, formatPhoneNumber, US_STATES } from '../constants';
import {
  StepTitle, FormSection, FormItem, SectionTitle, ErrorText,
  InfoBox, LargeInput, LargeSelect
} from '../styledComponents';

const { Paragraph, Text } = Typography;
const { Option } = Select;

const StepPersonalInfo = ({ values, errors, touched, setFieldValue }) => (
  <>
    <StepTitle level={4}>Personal & Contact Information</StepTitle>
    <InfoBox>
      <div className="icon"><InfoCircleOutlined /></div>
      <div className="content">
        <Paragraph style={{ margin: 0, color: colors.text }}>
          Your personal information helps us process your test request accurately. Contact information allows us to send you your results securely.
        </Paragraph>
      </div>
    </InfoBox>
    
    <FormSection>
      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <FormItem>
            <SectionTitle>First Name</SectionTitle>
            <Field name="firstName">
              {({ field }) => (
                <LargeInput
                  {...field}
                  placeholder="Enter your first name"
                  style={{ borderColor: touched.firstName && errors.firstName ? colors.error : null }}
                />
              )}
            </Field>
            <ErrorMessage name="firstName" component={ErrorText} />
          </FormItem>
        </Col>
        <Col xs={24} sm={12}>
          <FormItem>
            <SectionTitle>Last Name</SectionTitle>
            <Field name="lastName">
              {({ field }) => (
                <LargeInput
                  {...field}
                  placeholder="Enter your last name"
                  style={{ borderColor: touched.lastName && errors.lastName ? colors.error : null }}
                />
              )}
            </Field>
            <ErrorMessage name="lastName" component={ErrorText} />
          </FormItem>
        </Col>
      </Row>
      
      <FormItem>
        <SectionTitle>Date of Birth</SectionTitle>
        <DatePicker
          selected={values.dateOfBirth ? moment(values.dateOfBirth, "MM/DD/YYYY").toDate() : null}
          onChange={(date) => {
            const formattedDate = date ? moment(date).format("MM/DD/YYYY") : '';
            setFieldValue('dateOfBirth', formattedDate);
          }}
          dateFormat="MM/dd/yyyy"
          placeholderText="Select your date of birth"
          maxDate={new Date()}
          showYearDropdown
          showMonthDropdown
          dropdownMode="select"
          yearDropdownItemNumber={100}
          scrollableYearDropdown
          openToDate={new Date(1990, 0, 1)}
          customInput={
            <LargeInput 
              style={{
                borderColor: touched.dateOfBirth && errors.dateOfBirth ? colors.error : null
              }}
            />
          }
        />
        <ErrorMessage name="dateOfBirth" component={ErrorText} />
      </FormItem>
      
      <FormItem>
        <SectionTitle>Phone Number</SectionTitle>
        <Field name="phoneNumber">
          {({ field }) => (
            <LargeInput
              {...field}
              placeholder="(XXX) XXX-XXXX"
              style={{ borderColor: touched.phoneNumber && errors.phoneNumber ? colors.error : null }}
              onChange={(e) => {
                const formattedPhoneNumber = formatPhoneNumber(e.target.value);
                setFieldValue('phoneNumber', formattedPhoneNumber);
              }}
            />
          )}
        </Field>
        <ErrorMessage name="phoneNumber" component={ErrorText} />
      </FormItem>
      
      <FormItem>
        <SectionTitle>Email Address</SectionTitle>
        <Field name="emailAddress">
          {({ field }) => (
            <LargeInput
              {...field}
              placeholder="Enter your email address"
              type="email"
              style={{ borderColor: touched.emailAddress && errors.emailAddress ? colors.error : null }}
            />
          )}
        </Field>
        <ErrorMessage name="emailAddress" component={ErrorText} />
      </FormItem>
    </FormSection>

    <FormSection>
      <Divider style={{ margin: '0 0 16px' }}>
        <Text style={{ color: colors.text, fontSize: '14px' }}>Address Information</Text>
      </Divider>
      
      <FormItem>
        <SectionTitle>Street Address</SectionTitle>
        <Field name="addressLine1">
          {({ field }) => (
            <LargeInput
              {...field}
              placeholder="Enter your street address"
              style={{ borderColor: touched.addressLine1 && errors.addressLine1 ? colors.error : null }}
            />
          )}
        </Field>
        <ErrorMessage name="addressLine1" component={ErrorText} />
      </FormItem>
      
      <FormItem>
        <SectionTitle>Apartment, Suite, etc. (optional)</SectionTitle>
        <Field name="addressLine2">
          {({ field }) => (
            <LargeInput
              {...field}
              placeholder="Apartment, suite, unit, building, floor, etc."
            />
          )}
        </Field>
      </FormItem>
      
      <Row gutter={16}>
        <Col xs={24} sm={8}>
          <FormItem>
            <SectionTitle>City</SectionTitle>
            <Field name="city">
              {({ field }) => (
                <LargeInput
                  {...field}
                  placeholder="Enter your city"
                  style={{ borderColor: touched.city && errors.city ? colors.error : null }}
                />
              )}
            </Field>
            <ErrorMessage name="city" component={ErrorText} />
          </FormItem>
        </Col>
        <Col xs={24} sm={8}>
          <FormItem>
            <SectionTitle>State</SectionTitle>
            <LargeSelect
              style={{ 
                width: '100%',
                borderColor: touched.state && errors.state ? colors.error : null 
              }}
              placeholder="Select State"
              value={values.state}
              onChange={(value) => setFieldValue('state', value)}
            >
              {US_STATES.map((state) => (
                <Option key={state.value} value={state.value}>{state.label}</Option>
              ))}
            </LargeSelect>
            <ErrorMessage name="state" component={ErrorText} />
          </FormItem>
        </Col>
        <Col xs={24} sm={8}>
          <FormItem>
            <SectionTitle>Zip Code</SectionTitle>
            <Field name="zipCode">
              {({ field }) => (
                <LargeInput
                  {...field}
                  placeholder="Enter your zip code"
                  maxLength={5}
                  style={{ borderColor: touched.zipCode && errors.zipCode ? colors.error : null }}
                />
              )}
            </Field>
            <ErrorMessage name="zipCode" component={ErrorText} />
          </FormItem>
        </Col>
      </Row>
    </FormSection>
  </>
);

export default StepPersonalInfo;
